// The changes a person makes to a task, as patches: switching kind, Waiting on
// and off, ticking a one-off, and moving a task to a day or into the tray.
// Pure functions; the caller writes the patch and offers Undo with the
// `before` it returns.

import type { TaskPatch } from '@/data/tasks'
import type { DateStr, Task, TaskKind, TimeStr } from '@/types'
import { addDays, isoWeekday, startOfWeek } from './dates'
import { RECURRENCE_PRESETS } from './recurrence'
import { routineWeek } from './status'

/** The fields a kind switch can touch, as they were, for Undo. */
function snapshot(t: Task): TaskPatch {
  return {
    kind: t.kind,
    plannedDate: t.plannedDate,
    dueDate: t.dueDate,
    dueTime: t.dueTime,
    recurrence: t.recurrence,
    weekOf: t.weekOf,
    reminder: t.reminder,
    done: t.done,
    doneOn: t.doneOn,
    completedAt: t.completedAt,
  }
}

/**
 * Switching kind clears the fields the new kind does not use. A repeat needs a
 * due date, taken from the task's dates or today, and starts weekly on that
 * weekday. A routine has no due date and no rule; it keeps its Do it on day if
 * that falls in this week (a day already passed puts it in Not done),
 * otherwise it starts unplaced.
 */
export function kindChange(t: Task, kind: TaskKind, today: DateStr): { patch: TaskPatch; before: TaskPatch } | null {
  if (t.kind === kind) return null
  const before = snapshot(t)
  const reopen: TaskPatch = t.done ? { done: false, doneOn: null, completedAt: null } : {}
  if (kind === 'once') {
    return { patch: { kind, recurrence: null, weekOf: null }, before }
  }
  if (kind === 'repeat') {
    const due = t.dueDate ?? t.plannedDate ?? today
    const weekly = RECURRENCE_PRESETS.find((p) => p.key === 'weekly')!.make(due)
    return {
      patch: { ...reopen, kind, dueDate: due, recurrence: { ...weekly, scheduledTime: t.dueTime }, weekOf: null, plannedDate: t.plannedDate && t.plannedDate <= due ? t.plannedDate : null },
      before,
    }
  }
  const monday = startOfWeek(today)
  const inThisWeek = !!t.plannedDate && t.plannedDate >= monday && t.plannedDate <= addDays(monday, 6)
  return {
    patch: { ...reopen, kind, dueDate: null, dueTime: null, reminder: null, recurrence: null, weekOf: monday, plannedDate: inThisWeek ? t.plannedDate : null },
    before,
  }
}

/** The quick choices for "Follow up when?"; a custom date and time is always offered beside them. */
export const FOLLOW_UP_CHOICES: { label: string; days: number | null }[] = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'In a week', days: 7 },
  { label: 'No date', days: null },
]

/**
 * On a routine, the choices that fall after this Sunday: a routine has no
 * per-week exceptions, so its follow-up stays within its week.
 */
export function followUpChoicesFor(t: Pick<Task, 'kind'>, today: DateStr): typeof FOLLOW_UP_CHOICES {
  if (t.kind !== 'routine') return FOLLOW_UP_CHOICES
  const sunday = addDays(startOfWeek(today), 6)
  return FOLLOW_UP_CHOICES.filter((c) => c.days === null || addDays(today, c.days) <= sunday)
}

export function waitingOn(date: DateStr | null, time: TimeStr | null): TaskPatch {
  return { waiting: true, followUpDate: date, followUpTime: date ? time : null }
}

export function waitingOff(): TaskPatch {
  return { waiting: false, followUpDate: null, followUpTime: null }
}

/** Ticking a one-off. A waiting task stops waiting once it is done. */
export function doneOnce(t: Task, today: DateStr): { patch: TaskPatch; before: TaskPatch } {
  return {
    patch: { done: true, doneOn: today, waiting: false, followUpDate: null, followUpTime: null },
    before: { done: false, doneOn: null, completedAt: null, waiting: t.waiting, followUpDate: t.followUpDate, followUpTime: t.followUpTime },
  }
}

export function reopenOnce(): TaskPatch {
  return { done: false, doneOn: null, completedAt: null }
}

/**
 * A task dropped on a day, or given a Do it on day in the panel. It never
 * changes the due date. A waiting task with a follow-up day sits there, so a
 * drag moves the follow-up; a routine moves within the dropped day's week.
 */
export function moveToDay(t: Task, day: DateStr): TaskPatch {
  if (t.waiting && t.followUpDate) return { followUpDate: day }
  if (t.kind === 'routine') return { plannedDate: day, weekOf: startOfWeek(day) }
  return { plannedDate: day }
}

export type TrayGroup = 'routines' | 'done' | 'notdone' | 'nodate'

/**
 * A task dropped on a tray count or group, or its Do it on day cleared. On
 * Routines a routine loses its day for the week it is in. On No date a task
 * loses its Do it on day (a repeat goes back to the due date it comes round on). Null
 * means the drop is refused, with the reason.
 */
export function moveToTray(t: Task, group: TrayGroup, today: DateStr): { patch: TaskPatch; toast: string } | { refused: string } {
  if (group === 'routines') {
    if (t.kind !== 'routine') return { refused: 'Only routines go in Routines' }
    const week = t.weekOf && t.weekOf > startOfWeek(today) ? t.weekOf : routineWeek(t, today).weekOf
    return { patch: { plannedDate: null, weekOf: week, ...(t.waiting ? { followUpDate: null, followUpTime: null } : {}) }, toast: 'Moved to Routines' }
  }
  if (group === 'nodate') {
    if (t.kind === 'routine') return { refused: 'Routines go in Routines' }
    const patch: TaskPatch = { plannedDate: null, ...(t.waiting ? { followUpDate: null, followUpTime: null } : {}) }
    return { patch, toast: t.kind === 'repeat' && t.dueDate ? 'Back on its due day' : 'Moved to No date' }
  }
  return { refused: 'Drop it on a day, Routines or No date' }
}

/** The weekday a routine comes round on, for its card in later weeks. */
export function routineWeekday(t: Task, today: DateStr): number | null {
  const r = routineWeek(t, today)
  return r.plannedDate ? isoWeekday(r.plannedDate) : null
}
